import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { largeCurrencyFormat } from 'common/formats/formats';
import { LoadingSection } from 'components';
import { MILLIONS } from 'components/FeaturedSpreadsheet/constants';
import { ALEXANDRIA_METRICS, MetricUnitsByKey } from 'dashboard409a/services/alexandria/types';
import { formatNumbers } from 'utilities';
import { latestValue, MetricSeriesByCompany } from './compute/metrics';
import { CompRef, COMPUTED_METRICS } from './constants';

interface PerformanceTableProps {
  comps: CompRef[];
  byCompany: MetricSeriesByCompany;
  companyNames: Record<string, string>;
  isLoading: boolean;
  metricUnits: MetricUnitsByKey;
}

interface PerformanceColumn {
  header: string;
  metricKey: string;
  unitsMetricKey?: string;
}

const COLUMNS: PerformanceColumn[] = [
  { header: 'LTM Revenue ($MM)', metricKey: ALEXANDRIA_METRICS.LTM_REVENUE },
  { header: 'LTM Revenue Growth', metricKey: ALEXANDRIA_METRICS.LTM_REVENUE_GROWTH },
  {
    header: 'NTM Revenue Growth',
    metricKey: COMPUTED_METRICS.NTM_REVENUE_GROWTH,
    unitsMetricKey: ALEXANDRIA_METRICS.LTM_REVENUE_GROWTH,
  },
  { header: 'Gross Margin', metricKey: ALEXANDRIA_METRICS.GROSS_MARGIN },
  { header: 'EBITDA Margin', metricKey: ALEXANDRIA_METRICS.EBITDA_MARGIN },
];

const BORDER_COLOR = '#E3E8EF';
const TABLE_CURRENCY = { code: 'USD', units: MILLIONS };

// Normalize Alexandria currency values to absolute dollars with the catalog
// factor, then let Investor's shared formatter display them in millions.
const formatCell = (value: number | null, units: MetricUnitsByKey[string] | undefined): string => {
  if (value === null) {
    return '—';
  }
  if (units?.unit === 'ratio') {
    return `${(value * (units.factor ?? 1) * 100).toFixed(1)}%`;
  }
  return String(
    formatNumbers({
      value: value * (units?.factor ?? 1),
      format: largeCurrencyFormat,
      prefix: '',
      suffix: '',
      allowNegativeValue: true,
      currency: TABLE_CURRENCY,
      currencyCode: TABLE_CURRENCY.code,
      blankIfNull: false,
      staticCurrency: null,
    })
  );
};

// Latest performance metrics per comp. The subject company's row (from its
// own financials, not Alexandria) is appended once that data source exists.
const PerformanceTable = ({ comps, byCompany, companyNames, isLoading, metricUnits }: PerformanceTableProps) => {
  // Alphabetical: neutral across metrics and makes a company fast to find.
  const sortedComps = useMemo(
    () =>
      [...comps].sort((a, b) => (companyNames[a.ticker] ?? a.label).localeCompare(companyNames[b.ticker] ?? b.label)),
    [comps, companyNames]
  );

  if (isLoading) {
    return <LoadingSection />;
  }

  return (
    <Box>
      <Typography sx={{ color: 'text.primary', fontSize: '18px', fontWeight: 700, mb: 1 }} variant="subtitle2">
        Performance Metrics
      </Typography>
      <TableContainer component={Paper} sx={{ borderRadius: 2 }} variant="outlined">
        <Table
          size="small"
          sx={{
            '& td, & th': { border: `1px solid ${BORDER_COLOR}`, px: 2, py: 1.25 },
          }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', color: 'common.white', fontWeight: 700 }}>
                Company
              </TableCell>
              {COLUMNS.map(column => (
                <TableCell
                  key={column.header}
                  align="center"
                  sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', color: 'common.white', fontWeight: 700 }}>
                  {column.header.toUpperCase()}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedComps.map(comp => {
              const metrics = byCompany[comp.ticker];
              if (!metrics) {
                return null;
              }
              return (
                <TableRow key={comp.ticker} hover>
                  <TableCell>{companyNames[comp.ticker] ?? comp.label}</TableCell>
                  {COLUMNS.map(column => (
                    <TableCell key={column.header} align="center">
                      {formatCell(
                        latestValue(metrics[column.metricKey]),
                        metricUnits[column.unitsMetricKey ?? column.metricKey]
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PerformanceTable;
